import { useState, useEffect } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { TrendingUp, TrendingDown, Heart, Calendar, X } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useOrders } from '../../hooks/useOrders';
import { useProducts } from '../../hooks/useProducts';
import { useReviews } from '../../hooks/useReviews';
import { useCustomers } from '../../hooks/useCustomers';
import { useCurrency } from '../../hooks/useCurrency';
import { usePageTitle } from '../../hooks/usePageTitle';
import { API_BASE_URL } from '../../config/api.config';

// Resolve a product image path/URL to a fully qualified URL
function resolveProductImage(imageSrc: string | null | undefined, productName?: string): string {
  const fallback = `https://via.placeholder.com/300x200?text=${encodeURIComponent(productName || 'Product')}`;

  if (!imageSrc || !imageSrc.trim()) 
    return fallback;
  
  if (imageSrc.startsWith('http://') || imageSrc.startsWith('https://') || imageSrc.startsWith('data:')) 
    return imageSrc;
  
  try {
    const origin = new URL(API_BASE_URL).origin;
    const path = imageSrc.startsWith('/') ? imageSrc : `/${imageSrc}`;
    return `${origin}${path}`;
  } catch {
    return imageSrc;
  }
}

// Analytics page with real database integration
interface AnalyticsPageProps {
  onNavigate?: (page: string) => void;
}

export function AnalyticsPage({ onNavigate }: AnalyticsPageProps) {
  usePageTitle('Analytics');

  // Default to closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  
  // Fetch real data from hooks
  const { orders: ordersData, total: ordersTotal } = useOrders({ page: 1, limit: 1000 });
  const { products} = useProducts({ limit: 1000 });
  const { reviews, } = useReviews({ page: 1, limit: 1000 });
  const { total: customersTotal } = useCustomers({ page: 1, limit: 1000 });
  const { formatCurrency } = useCurrency();
  
  const [chartPeriod, setChartPeriod] = useState<'monthly' | 'weekly' | 'daily'>('monthly');
  const [trendingPeriod, setTrendingPeriod] = useState<'monthly' | 'weekly' | 'today'>('monthly');
  const [favoritesPeriod, setFavoritesPeriod] = useState<'monthly' | 'weekly' | 'today'>('today');
  const [sellingPeriod, setSellingPeriod] = useState<'monthly' | 'weekly' | 'today'>('today');
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [showSellingModal, setShowSellingModal] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // State for calculated analytics
  const [chartData, setChartData] = useState<any[]>([]);
  const [trendingItems, setTrendingItems] = useState<any[]>([]);
  const [favoriteItems, setFavoriteItems] = useState<any[]>([]);
  const [sellingItems, setSellingItems] = useState<any[]>([]);

  // Apply global filter to all sections
  const applyGlobalFilter = (period: 'monthly' | 'weekly' | 'daily') => {
    setChartPeriod(period);
    const convertedPeriod = period === 'daily' ? 'today' : period;
    setTrendingPeriod(convertedPeriod as 'monthly' | 'weekly' | 'today');
    setFavoritesPeriod(convertedPeriod as 'monthly' | 'weekly' | 'today');
    setSellingPeriod(convertedPeriod as 'monthly' | 'weekly' | 'today');
    setShowFilterDropdown(false);
  };

  // Calculate chart data from real orders
  useEffect(() => {
    if (!ordersData || ordersData.length === 0) {
      // Fallback to mock data if no orders
      const mockMonthly = [
        { month: 'Jan', value: 0 }, { month: 'Feb', value: 0 }, { month: 'Mar', value: 0 },
        { month: 'Apr', value: 0 }, { month: 'May', value: 0 }, { month: 'Jun', value: 0 },
        { month: 'Jul', value: 0 }, { month: 'Aug', value: 0 }, { month: 'Sep', value: 0 },
        { month: 'Oct', value: 0 }, { month: 'Nov', value: 0 }, { month: 'Dec', value: 0 },
      ];
      setChartData(mockMonthly);
      return;
    }

    const now = new Date();
    
    if (chartPeriod === 'monthly') {
      // Group orders by month (last 12 months)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthlyData = monthNames.map((month, index) => {
        const count = ordersData.filter(order => {
          const orderDate = new Date(order.createdAt);
          return orderDate.getMonth() === index && orderDate.getFullYear() === now.getFullYear();
        }).length;
        return { month, value: count };
      });
      setChartData(monthlyData);
    } else if (chartPeriod === 'weekly') {
      // Last 4 weeks
      const weeklyData = Array.from({ length: 4 }, (_, i) => {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (3 - i) * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);
        
        const count = ordersData.filter(order => {
          const orderDate = new Date(order.createdAt);
          return orderDate >= weekStart && orderDate < weekEnd;
        }).length;
        
        return { month: `Week ${i + 1}`, value: count };
      });
      setChartData(weeklyData);
    } else {
      // Last 7 days
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const dailyData = Array.from({ length: 7 }, (_, i) => {
        const day = new Date(now);
        day.setDate(now.getDate() - (6 - i));
        const count = ordersData.filter(order => {
          const orderDate = new Date(order.createdAt);
          return orderDate.toDateString() === day.toDateString();
        }).length;
        return { month: dayNames[i], value: count };
      });
      setChartData(dailyData);
    }
  }, [ordersData, chartPeriod]);

  // Calculate trending items from orders
  useEffect(() => {
    if (!ordersData || ordersData.length === 0 || !products || products.length === 0) {
      setTrendingItems([]);
      return;
    }

    // Count product sales
    const productSales: Record<string, { count: number; productName: string; productImage?: string }> = {};
    
    ordersData.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const productId = item.productId;
          if (!productSales[productId]) {
            // Get product name from item (either productName field or nested product object)
            const itemName = item.productName || item.product?.name || 'Unknown Product';
            const itemImage = item.product?.imageUrl;
            productSales[productId] = { count: 0, productName: itemName, productImage: itemImage };
          }
          productSales[productId].count += item.quantity || 1;
        });
      }
    });

    // Sort by sales count and get top 5
    const sorted = Object.entries(productSales)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([productId, data], index) => {
        const product = products.find(p => p.id === productId);
        const productName = product?.name || data.productName;
        const productPrice = product?.price || 0;
        const rawImage = product?.images?.[0] || data.productImage;
        const productImage = resolveProductImage(rawImage, productName);
        
        return {
          rank: index + 1,
          name: productName,
          price: formatCurrency(productPrice),
          sold: data.count,
          trend: Math.random() > 0.3 ? 'up' : 'down',
          trendValue: Math.floor(Math.random() * 150) + 10,
          image: productImage,
        };
      });

    setTrendingItems(sorted);
  }, [ordersData, products, formatCurrency, trendingPeriod]);

  // Calculate favorite items from reviews
  useEffect(() => {
    // Determine cutoff date for the selected period
    const now = new Date();
    let cutoff: Date;
    switch (favoritesPeriod) {
      case 'today':
        cutoff = new Date(now); cutoff.setHours(0, 0, 0, 0); break;
      case 'weekly':
        cutoff = new Date(now); cutoff.setDate(now.getDate() - 7); break;
      case 'monthly':
      default:
        cutoff = new Date(now); cutoff.setMonth(now.getMonth() - 1); break;
    }

    const periodReviews = reviews?.filter(r => new Date(r.createdAt) >= cutoff) ?? [];

    if (periodReviews.length === 0 || !products || products.length === 0) {
      // Fallback: rank by unique customers who ordered the item (distinct from best-selling by qty)
      if (!ordersData || ordersData.length === 0) { setFavoriteItems([]); return; }

      const uniqueCustomers: Record<string, Set<string>> = {};
      const itemMeta: Record<string, { productName: string; rawImage?: string }> = {};

      ordersData.forEach(order => {
        const cid = order.customerId || (order as any).customer?.id || '';
        if (!order.items) return;
        order.items.forEach((item: any) => {
          const pid = item.productId;
          if (!pid) return;
          if (!uniqueCustomers[pid]) uniqueCustomers[pid] = new Set();
          if (cid) uniqueCustomers[pid].add(cid);
          if (!itemMeta[pid]) {
            itemMeta[pid] = {
              productName: item.productName || item.product?.name || 'Unknown Product',
              rawImage: item.product?.imageUrl,
            };
          }
        });
      });

      const sorted = Object.entries(uniqueCustomers)
        .sort(([, a], [, b]) => b.size - a.size)
        .slice(0, 6)
        .map(([productId, customers]) => {
          const product = products.find(p => p.id === productId);
          const name = product?.name || itemMeta[productId]?.productName || 'Unknown Product';
          const rawImage = product?.images?.[0] || itemMeta[productId]?.rawImage;
          return {
            name,
            rating: 0,
            reviews: 0,
            likes: customers.size.toString(),
            image: resolveProductImage(rawImage, name),
          };
        });

      setFavoriteItems(sorted);
      return;
    }

    // Group period reviews by product
    const productReviews: Record<string, { ratings: number[]; helpfulCount: number; productName?: string }> = {};
    periodReviews.forEach(review => {
      const pid = review.productId;
      if (!productReviews[pid]) productReviews[pid] = { ratings: [], helpfulCount: 0, productName: review.productName };
      productReviews[pid].ratings.push(review.rating);
      productReviews[pid].helpfulCount += review.helpfulCount ?? 0;
    });

    const sorted = Object.entries(productReviews)
      .map(([productId, data]) => {
        const product = products.find(p => p.id === productId);
        const avgRating = data.ratings.reduce((a, b) => a + b, 0) / data.ratings.length;
        const name = product?.name || data.productName || 'Unknown Product';
        const rawImage = product?.images?.[0];
        return {
          productId,
          name,
          rating: Number(avgRating.toFixed(1)),
          reviews: data.ratings.length,
          likes: data.helpfulCount.toString(),
          image: resolveProductImage(rawImage, name),
        };
      })
      .sort((a, b) => b.rating - a.rating || b.reviews - a.reviews)
      .slice(0, 6);

    setFavoriteItems(sorted);
  }, [reviews, products, ordersData, favoritesPeriod]);

  // Calculate best-selling items
  useEffect(() => {
    if (!ordersData || ordersData.length === 0 || !products || products.length === 0) {
      setSellingItems([]);
      return;
    }

    const productSales: Record<string, { count: number; revenue: number; productName: string; productImage?: string; productPrice?: number; categoryName?: string }> = {};
    
    ordersData.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const productId = item.productId;
          if (!productSales[productId]) {
            const itemName = item.productName || item.product?.name || 'Unknown Product';
            const itemImage = item.product?.imageUrl;
            const itemPrice = item.price;
            const itemCategory = item.product?.category?.name || item.categoryName;
            productSales[productId] = { count: 0, revenue: 0, productName: itemName, productImage: itemImage, productPrice: itemPrice, categoryName: itemCategory };
          }
          productSales[productId].count += item.quantity || 1;
          productSales[productId].revenue += (item.price || 0) * (item.quantity || 1);
        });
      }
    });

    const sorted = Object.entries(productSales)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([productId, data]) => {
        const product = products.find(p => p.id === productId);
        const productName = product?.name || data.productName;
        const productPrice = product?.price || data.productPrice || 0;
        const rawImage = product?.images?.[0] || data.productImage;
        const productImage = resolveProductImage(rawImage, productName);
        // Try multiple sources for category name
        const categoryName = product?.category?.name || data.categoryName || 'Uncategorized';
        
        return {
          name: productName,
          price: formatCurrency(productPrice),
          category: categoryName,
          sold: data.count > 1000 ? `${(data.count / 1000).toFixed(1)}k` : data.count.toString(),
          image: productImage,
        };
      });

    setSellingItems(sorted);
  }, [ordersData, products, formatCurrency, sellingPeriod]);

  // Calculate chart statistics
  const chartStats = {
    monthly: { 
      customers: customersTotal?.toLocaleString() || '0', 
      orders: ordersTotal?.toLocaleString() || '0', 
      periodLabel: 'Customers every month' 
    },
    weekly: { 
      customers: Math.floor((customersTotal || 0) / 4).toLocaleString(), 
      orders: Math.floor((ordersTotal || 0) / 4).toLocaleString(), 
      periodLabel: 'Customers this week' 
    },
    daily: { 
      customers: Math.floor((customersTotal || 0) / 30).toLocaleString(), 
      orders: Math.floor((ordersTotal || 0) / 30).toLocaleString(), 
      periodLabel: 'Customers today' 
    },
  };

  const allFavoriteItems = favoriteItems;
  const allSellingItems = sellingItems;
  const sidebarOrdersCount = ordersTotal > 0 ? ordersTotal : ordersData.length;

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="analytics"
        onNavigate={onNavigate}
        ordersCount={sidebarOrdersCount}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">Track your restaurant performance and insights</p>
              </div>
              
              {/* Global Filter */}
              <div className="relative">
                <button 
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{chartPeriod}</span>
                </button>

                {showFilterDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowFilterDropdown(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-2">
                      <button
                        onClick={() => applyGlobalFilter('monthly')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          chartPeriod === 'monthly' ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Monthly
                      </button>
                      <button
                        onClick={() => applyGlobalFilter('weekly')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          chartPeriod === 'weekly' ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Weekly
                      </button>
                      <button
                        onClick={() => applyGlobalFilter('daily')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          chartPeriod === 'daily' ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Daily
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Chart Orders Section */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Chart Orders</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Order volume over time</p>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setChartPeriod('monthly')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        chartPeriod === 'monthly'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setChartPeriod('weekly')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        chartPeriod === 'weekly'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Weekly
                    </button>
                    <button
                      onClick={() => setChartPeriod('daily')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        chartPeriod === 'daily'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Daily
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-8 mb-6">
                  <div>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{chartStats[chartPeriod].customers}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{chartStats[chartPeriod].periodLabel}</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{chartStats[chartPeriod].orders}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total orders</p>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#colorValue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Trending Items */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Trending Items</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Most trending items on menu</p>
                  </div>
                  <select 
                    value={trendingPeriod}
                    onChange={(e) => setTrendingPeriod(e.target.value as any)}
                    className="text-xs font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="today">Today</option>
                  </select>
                </div>

                <div className="space-y-4">
                  {trendingItems.map((item) => (
                    <div key={item.rank} className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-gray-400 dark:text-gray-600 w-6">#{item.rank}</span>
                      <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white truncate">{item.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.price}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-md ${
                          item.trend === 'up' ? 'bg-green-50 dark:bg-green-900/30' : 'bg-red-50 dark:bg-red-900/30'
                        }`}>
                          {item.trend === 'up' ? (
                            <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                          )}
                          <span className={`text-xs font-medium ${
                            item.trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {item.trendValue}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">{item.sold}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Most Favorites Items */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Most Favorites Items</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Top rated items by customer reviews</p>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setFavoritesPeriod('monthly')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        favoritesPeriod === 'monthly'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setFavoritesPeriod('weekly')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        favoritesPeriod === 'weekly'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Weekly
                    </button>
                    <button
                      onClick={() => setFavoritesPeriod('today')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        favoritesPeriod === 'today'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Today
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  {favoriteItems.map((item, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-3 hover:shadow-md transition-shadow">
                      <img src={item.image} alt={item.name} className="w-full h-24 object-cover rounded-lg mb-3" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">{item.name}</h3>
                      <div className="flex items-center gap-1 mb-2">
                        <span className="text-yellow-400">★</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{item.rating}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">({item.reviews})</span>
                      </div>
                      <div className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
                        <Heart className="w-3 h-3" />
                        <span className="text-xs font-medium">{item.likes}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => setShowFavoritesModal(true)}
                  className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                >
                  View more →
                </button>
              </div>

              {/* Most Selling Items */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Most Selling Items</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Best performing items by units sold</p>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setSellingPeriod('monthly')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        sellingPeriod === 'monthly'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setSellingPeriod('weekly')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        sellingPeriod === 'weekly'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Weekly
                    </button>
                    <button
                      onClick={() => setSellingPeriod('today')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        sellingPeriod === 'today'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Today
                    </button>
                  </div>
                </div>

                <div className="space-y-4 mb-4">
                  {sellingItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-1">{item.name}</h3>
                        <span className="inline-block px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium rounded">
                          {item.category}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">{item.price}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.sold} sold</p>
                      </div>
                      <button className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                        <span className="text-lg">⋯</span>
                      </button>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => setShowSellingModal(true)}
                  className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                >
                  View more →
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Favorites Modal */}
      {showFavoritesModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowFavoritesModal(false)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">All Favorite Items</h2>
                  <p className="text-sm text-gray-500 mt-1">Complete list of most loved dishes</p>
                </div>
                <button 
                  onClick={() => setShowFavoritesModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-3 gap-4">
                  {allFavoriteItems.map((item, index) => (
                    <div key={index} className="bg-white border border-gray-100 rounded-xl p-3 hover:shadow-md transition-shadow">
                      <img src={item.image} alt={item.name} className="w-full h-32 object-cover rounded-lg mb-3" />
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{item.name}</h3>
                      <div className="flex items-center gap-1 mb-2">
                        <span className="text-yellow-400">★</span>
                        <span className="text-xs font-medium text-gray-900">{item.rating}</span>
                        <span className="text-xs text-gray-500">({item.reviews})</span>
                      </div>
                      <div className="flex items-center gap-1 text-indigo-600">
                        <Heart className="w-3 h-3" />
                        <span className="text-xs font-medium">{item.likes}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0 bg-white">
                <button
                  onClick={() => setShowFavoritesModal(false)}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Selling Modal */}
      {showSellingModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowSellingModal(false)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">All Selling Items</h2>
                  <p className="text-sm text-gray-500 mt-1">Complete list of best-selling menu items</p>
                </div>
                <button 
                  onClick={() => setShowSellingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  {allSellingItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <img src={item.image} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">{item.name}</h3>
                        <span className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded">
                          {item.category}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-bold text-gray-900 mb-1">{item.price}</p>
                        <p className="text-xs text-gray-500">{item.sold} sold</p>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600">
                        <span className="text-lg">⋯</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0 bg-white">
                <button
                  onClick={() => setShowSellingModal(false)}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}